/**
 * @file        src/lib/goalToken.ts
 * @description Turns an arbitrary human label into a goal identifier that
 *              Metrika and our own collector both accept. Used wherever a
 *              goal id is COMPOSED at runtime from data (a place label, an
 *              A/B variant) rather than written out by hand.
 * @runs        Anywhere (pure string function, no browser API).
 * @universal   goalToken — the ONE normaliser for composed goal ids. Pairs
 *              with trackGoal from src/lib/metrika.ts. Registered in
 *              knowledge/universals/tools.md.
 *
 * @rule        THE ALPHABET IS [a-z0-9_] AND NOTHING ELSE. Both ends enforce
 *              it: Metrika's JavaScript-event identifiers are documented that
 *              way, and /api/track drops anything failing
 *              /^[a-z0-9_]{1,64}$/ — silently, with a 204, because the
 *              collector may never break the click it is measuring. So a goal
 *              id with a hyphen or a capital letter is not "slightly wrong":
 *              it is counted nowhere at all, in Metrika or in the journal,
 *              and nothing anywhere reports the loss. This function exists
 *              because that is exactly what happened to the stage-Б funnel
 *              goals, which were composed as `form_view_main-hero_B` from a
 *              kebab-case place label and an upper-case variant name (series
 *              home-stage-b, step B04d).
 * @rule        NORMALISE AT COMPOSITION, NOT INSIDE trackGoal. trackGoal
 *              sends the identifier it is given, unchanged — dozens of
 *              hand-written goal ids already pass through it and match goals
 *              configured in the Metrika UI. Rewriting ids there would
 *              quietly rename them. Call this function where the id is built.
 * @rule        THE OUTPUT IS A STABLE KEY, so changing this function renames
 *              every composed goal and orphans the ones already created in
 *              the Metrika UI. Treat a change here as a data migration.
 */

/** Longest id /api/track will store; also Metrika's practical ceiling. */
const MAX_GOAL_ID_LENGTH = 64;

/**
 * Normalise one fragment of a composed goal id.
 *
 * Lower-cases, collapses every run of characters outside [a-z0-9] into a
 * single underscore, and trims underscores from both ends.
 *
 * @param raw Any label: "main-hero", "B-nobudget", "tools/calculator".
 * @returns A fragment safe to join with "_", or "unknown" when nothing
 *          usable survives (an empty fragment would produce a double
 *          underscore and a meaningless goal).
 *
 * @example
 *   goalToken("main-hero")    // "main_hero"
 *   goalToken("B-nobudget")   // "b_nobudget"
 *   goalToken("")             // "unknown"
 */
export function goalToken(raw: string): string {
  const normalised = raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, MAX_GOAL_ID_LENGTH);
  return normalised === "" ? "unknown" : normalised;
}
